// loads the tree of the Dicta corpus
// also provides a utility function 'treeSort' which sorts a list according to an in-order traversal of the tree
import axios from 'axios'
import {summarizeKeys, Text} from './index'

function initService (bibleMode) {
  return axios.get('static/' + (
    bibleMode
      ? 'corpusData/Bible.json'
      : 'corpusData/corpusTree.json'))
    .then(function (response) {
      service.corpusTree = response.data
      service.loaded = false
      service.loadNode(null)
    })
}

const service = {
  corpusTree: [],
  nodeLoadPromises: [],
  readyPromise: initService(true),
  changeMode (bibleMode) {
    service.readyPromise = initService(bibleMode)
    return service.readyPromise
  },
  loadNode (node) {
    if (node == null) {
      if (service.loaded) {
        return Promise.resolve(null)
      }
    } else if (node.loaded) {
      return Promise.resolve(node)
    }
    const children = node ? node.children : service.corpusTree
    const requests = []
    for (let i = 0; i < children.length; i++) {
      const child = children[i]
      // add links back to the parent, so we can find the parent to update selections
      child['parent'] = node
      service.$keyToNode[child['key']] = child
      if (typeof (child.children) === 'string') {
        requests.push(axios.get('static/corpusData/' + child.children).then(function (response) {
          this.children = response.data
        }.bind(child)))
      }
    }
    return Promise.all(requests).then(function () {
      if (node) {
        node.loaded = true
      } else {
        service.loaded = true
      }
      return node
    })
  },
  $keyToNode: {},
  keyToNode (key) {
    function loadOneLevel (nodeChildren, path) {
      console.log('path:')
      console.log(path)
      let request = null
      const childList = nodeChildren ? nodeChildren.filter(child => child.key === path) : []
      if (childList.length > 0) {
        const node = childList[0]
        // if (node) console.log('calling "loadNode" for node with key: ' + node.key)
        request = service.loadNode(node)
      } else {
        if (path.length) {
          console.log('stripping last level and recursing')
          return loadOneLevel(nodeChildren, path.split('/').slice(0, -1).join('/'))
        } else return null
      }
      let returnPromise = request.then(node => node.children)
      service.nodeLoadPromises.push(returnPromise)
      return returnPromise
    }

    if (!service.$keyToNode.hasOwnProperty(key)) {
      const pathElements = key.split('/')
      let path = ''
      let lastRequest
      pathElements.forEach(name => {
        if (path.length === 0) {
          path = name
        } else {
          path = path + '/' + name
        }
        if (lastRequest) {
          lastRequest = lastRequest.then((nodeChildren) => loadOneLevel(nodeChildren, path))
          service.nodeLoadPromises.push(lastRequest)
        } else {
          console.log('running loadOneLevel on service.corpusTree for key ' + key + ', current name: ' + name)
          lastRequest = loadOneLevel(service.corpusTree, path)
        }
      })
    }
    return service.$keyToNode[key]
  },
  // list - the list to be sorted
  // getKeyFunc - a function that takes a list item and returns its key
  treeSort (list, getKeyFuncParam) {
    // copies the array so that this doesn't modify the original
    let listCopy = list.slice()
    const getKeyFunc = getKeyFuncParam || (x => x)
    const getTreeKey = key => getKeyFunc(key).replace(/^\/Dicta Corpus\//, '')
    // walk the tree. nextItems holds the remaining nodes to walk
    let nextItems = service.corpusTree.slice()
    const sortedItems = []
    while (nextItems.length > 0) {
      const currentItem = nextItems.shift()
      // loop from the end, so that removing an item doesn't affect the rest of the loop
      for (let i = listCopy.length - 1; i >= 0; i--) {
        if (currentItem.key === getTreeKey(listCopy[i])) {
          sortedItems.push(listCopy[i])
          listCopy.splice(i, 1)
        }
      }
      // if there are child nodes and this node is a prefix of a key we are sorting, add to nextItems
      if (
        Array.isArray(currentItem['children']) &&
        listCopy.some(sortItem => getTreeKey(sortItem).startsWith(currentItem.key))
      ) {
        nextItems = currentItem.children.concat(nextItems)
      }
    }
    // if there are any items left, leave them unsorted at the end
    return sortedItems.concat(listCopy)
  }
}

function $getNewBreadCrumbs () {
  return [{
    title: 'Collections',
    children: service.corpusTree
  }]
}

function $initSelectionObject (text) {
  const selectionObject = this
  service.readyPromise.then(function () {
    selectionObject.$treeNode = service.corpusTree
    selectionObject.breadCrumbs = selectionObject.$getNewBreadCrumbs()

    // list selected nodes by key
    if (text && text.keys) {
      selectionObject.$selectedNodes = text.keys.map(function (key) {
        return key.substring('/Dicta Corpus/'.length)
      })
    } else {
      selectionObject.$selectedNodes = []
    }
    selectionObject.$recalculatePartials()
    selectionObject.$updatePreview(text)
  })
}

// end of tree setup; helper functions come next

function $expandNode (itemTitle) {
  const selectionObject = this
  const currentNode = this.$treeNode
  for (let i = 0; i < currentNode.length; i++) {
    if (currentNode[i]['title'] === itemTitle) {
      const node = currentNode[i]
      return service.loadNode(node)
        .then(function (loadedNode) {
          selectionObject.$treeNode = loadedNode.children
          selectionObject.breadCrumbs.push({
            title: itemTitle,
            children: loadedNode.children
          })
        })
    }
  }
}

// remove current item from array if 'ancestor' is an ancestor
function removeIfAncestor (arr, idx, ancestor) {
  let current = service.keyToNode(arr[idx])
  while (current['parent'] != null) {
    if (current['parent'] === ancestor) {
      arr.splice(idx, 1)
      break
    }
    current = current['parent']
  }
}

function finishRecalculation (ktnReturn, originalThis) {
  let parent = ktnReturn['parent']
  while (parent != null) {
    if (originalThis.partialNodes.indexOf(parent['key']) === -1) {
      originalThis.partialNodes.push(parent['key'])
    }
    parent = parent['parent']
  }
}

function $recalculatePartials () {
  this.partialNodes = []
  for (let i = 0; i < this.$selectedNodes.length; i++) {
    let ktnReturn = service.keyToNode(this.$selectedNodes[i])
    let originalThis = this
    if (ktnReturn === undefined) {
      // console.log('ktnReturn:')
      // console.log(ktnReturn)
      Promise.all(service.nodeLoadPromises).then(() => {
        service.nodeLoadPromises = []
        /*
        if (service.$keyToNode[this.$selectedNodes[i]] === undefined) {
          console.log('key:')
          console.log(this.$selectedNodes[i])
          console.log('service.$keyToNode keys "at the time":')
          console.log(JSON.parse(JSON.stringify(Object.keys(service.$keyToNode))))
          console.log('service.$keyToNode "now":')
          console.log(service.$keyToNode)
        }
        */
        finishRecalculation(service.$keyToNode[this.$selectedNodes[i]], originalThis)
      })
    } else finishRecalculation(ktnReturn, originalThis)
  }
}

function $isNodeSelected (node) {
  const parent = service.keyToNode(node)['parent']
  return this.$selectedNodes.indexOf(node) > -1 || (parent != null && this.$isNodeSelected(parent['key']))
}

function $isNodePartial (node) {
  return this.partialNodes.indexOf(node) > -1
}

// toggle selection for a given node by key
function $toggleSelection (itemKey) {
  // is currently selected, so this deselects
  if (this.$isNodeSelected(itemKey)) {
    // currently selected itself?
    let idx = this.$selectedNodes.indexOf(itemKey)
    if (idx > -1) {
      // yes, so remove it.
      this.$selectedNodes.splice(idx, 1)
    } else {
      // select siblings and deselect ancestor
      let currentNode = service.keyToNode(itemKey)
      while ((idx = this.$selectedNodes.indexOf(currentNode['key'])) === -1) {
        const children = currentNode['parent']['children']
        for (let i = 0; i < children.length; i++) {
          if (children[i] !== currentNode) {
            this.$selectedNodes.push(children[i]['key'])
          }
        }
        currentNode = currentNode['parent']
      }
      this.$selectedNodes.splice(idx, 1)
    }
  } else { // is newly selected
    // select this
    this.$selectedNodes.push(itemKey)
    // check if all siblings are selected
    const parent = service.keyToNode(itemKey)['parent']
    if (parent != null) {
      let sibCount = 0
      const siblings = parent['children']
      for (let i = 0; i < siblings.length; i++) {
        if (this.$selectedNodes.indexOf(siblings[i]['key']) > -1) {
          sibCount++
        }
      }
      // if so, select the parent
      if (sibCount === siblings.length) {
        this.$toggleSelection(parent['key'])
      }
    }
  }

  // remove any descendants already selected
  for (let i = this.$selectedNodes.length - 1; i >= 0; i--) {
    removeIfAncestor(this.$selectedNodes, i, service.keyToNode(itemKey))
  }

  this.$recalculatePartials()

  this.$selectedNodes = service.treeSort(this.$selectedNodes, key => key)
  this.selectionText = {
    keys: this.$selectedNodes.map(key => '/Dicta Corpus/' + key)
  }

  this.hasKeys = this.$selectedNodes.length > 0

  this.$updatePreview(this.selectionText)
}

function $actuallyLoadPreview () {
  const selectionObject = this
  this.$previewWaiting = false
  axios.post(process.env.SERVER_URL + '/DictaDatabaseServer/api/TextFeatures/GetTextLargeAndSmall', this.$previewKeys)
    .then(function (response) {
      selectionObject.previewLoading = false
      selectionObject.previewText = response.data
    })
    .catch(() => null)
}

function $updatePreview (selectedText) {
  if (!selectedText || !selectedText.keys || selectedText.keys.length === 0) {
    this.previewText = null
    return
  }
  this.$previewKeys = selectedText.keys
  this.previewLoading = true
  if (this.$previewWaiting === false) {
    this.$previewWaiting = true
    setTimeout($actuallyLoadPreview.bind(this), 1000)
  }
}

function selectCrumb (crumbNumber) {
  this.breadCrumbs = this.breadCrumbs.slice(0, crumbNumber + 1)
  this.$treeNode = this.breadCrumbs[crumbNumber].children
}

function goUp () {
  if (this.breadCrumbs.length > 1) {
    this.selectCrumb(this.breadCrumbs.length - 2)
  }
}

function toggleAll () {
  if (this.$treeNode[0]['parent'] !== null) {
    this.$toggleSelection(this.$treeNode[0]['parent'].key)
  } else {
    const allSelected = this.$treeNode.every(node => this.$isNodeSelected(node.key))
    this.$treeNode.forEach(node => {
      const key = node.key
      if (!allSelected) {
        if (!this.$isNodeSelected(key)) {
          this.$toggleSelection(key)
        }
      } else {
        this.$toggleSelection(key)
      }
    })
  }
}

function areAllChecked () {
  return this.$treeNode.every(node => this.$isNodeSelected(node.key))
}

function $isNodeLeaf (node) {
  return typeof (node.children) !== 'string' && node.children.length === 0
}

class TreeItem {
  constructor (node, selectionObject) {
    this.node = node
    this.selectionObject = selectionObject
  }
  isItemSelected () {
    return this.selectionObject.$isNodeSelected(this.node.key)
  }
  isItemPartial () {
    return this.selectionObject.$isNodePartial(this.node.key)
  }
  toggleSelection () {
    return this.selectionObject.$toggleSelection(this.node.key)
  }
  get title () {
    return this.node.title
  }
  expandItem () {
    return this.selectionObject.$expandNode(this.node.title)
  }
  isItemLeaf () {
    return this.selectionObject.$isNodeLeaf(this.node)
  }
}

let selectionObjectCounter = 0

service.getSelectionObject = function () {
  const selectionObject = {
    breadCrumbs: $getNewBreadCrumbs,
    previewLoading: false,
    previewText: null,
    hasKeys: false,
    get treeItems () {
      return this.$treeNode.map(child => new TreeItem(child, this))
    },
    get keys () {
      return this.$selectedNodes.map(key => '/Dicta Corpus/' + key)
    },
    $text: null,
    get text () {
      return new Text(this.keys, this.name)
    },
    name: null,
    id: selectionObjectCounter++,
    selectCrumb,
    goUp,
    toggleAll,
    $treeNode: [],
    $selectedNodes: [],
    $expandNode,
    $isNodeSelected,
    $isNodePartial,
    $toggleSelection,
    $recalculatePartials,
    $initSelectionObject,
    $updatePreview,
    $getNewBreadCrumbs,
    summarizeText () {
      return summarizeKeys(this.keys)
    },
    $isNodeLeaf,
    areAllChecked,
    $previewKeys: [],
    $previewWaiting: false,
    initializeFromText (text) {
      this.$initSelectionObject(text)
    }
  }
  selectionObject.$initSelectionObject()
  return selectionObject
}

export default service



// WEBPACK FOOTER //
// ./src/texts/treeService.js